import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BookingModal from '../../components/booking/BookingModal/BookingModal';
import ConsultationServiceCard from '../../components/booking/ConsultationServiceCard/ConsultationServiceCard';
import ConnectionError from '../../components/ConnectionError/ConnectionError';
import useReveal from '../../hooks/useReveal';
import { publicContentService } from '../../services/publicContentService';
import type { ConsultationService, ConsultationServiceItem } from '../../types';
import './Consultation.css';


function Consultation() {
  const { t, i18n } = useTranslation();
  const [selectedService, setSelectedService] = useState<ConsultationService | null>(null);
  const [services, setServices] = useState<ConsultationServiceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const isArabic = i18n.resolvedLanguage?.startsWith('ar') ?? false;

  const loadServices = () => {
    setLoading(true);
    setError(false);
    publicContentService
      .getConsultationServices()
      .then((data) => setServices(data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadServices();
  }, []);

  useReveal([services.length, loading]);

  // Map API services to the shape ConsultationServiceCard / BookingModal expect
  const items = useMemo<ConsultationService[]>(
    () =>
      services.map((srv) => ({
        id: srv.id,
        title: isArabic ? srv.titleAr : srv.titleEn,
        summary: (isArabic ? srv.summaryAr : srv.summaryEn) || '',
        image: srv.imageUrl || '',
        durationMinutes: srv.durationMinutes ?? 60,
      })),
    [services, isArabic],
  );

  return (
    <main className="page-main">
      <section className="consult-section">
        <div className="consult-container">
          <h1 className="consult-heading reveal">{t('consultation.title')}</h1>

          {loading ? (
            <div className="admin-loading">
              <span className="auth-spinner auth-spinner-brand" />
            </div>
          ) : error ? (
            <ConnectionError onRetry={loadServices} />
          ) : (
            <div className="consult-grid">
              {items.map((item, i) => (
                <ConsultationServiceCard
                  key={item.id}
                  service={item}
                  delayIndex={i + 1}
                  onBook={setSelectedService}
                />
              ))}
            </div>
          )}

          <p className="consult-desc reveal">{t('consultation.desc')}</p>
        </div>
      </section>

      <BookingModal
        service={selectedService}
        isOpen={selectedService !== null}
        onClose={() => setSelectedService(null)}
      />
    </main>
  );
}

export default Consultation;
